import { Badge, Chip, Switch, Typography, useTheme } from "@mui/joy";
import { Hidden, Tooltip, useMediaQuery } from "@mui/material";
import { Box } from "@mui/system";
import { DataGridPro, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { NerdPaginationFooter } from "@nerdjs/nerd-ui";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EmailsSearch } from "./emailSearch";
import { useSelector } from "react-redux";
import { useLocalStorage } from "@uidotdev/usehooks";
import { Email } from "../../entities/email";
import { useEmails } from "../../redux/local420Emails/emailHooks";
import Id from "../id/id";

const EMAILS_PER_PAGE = 50;

export default function EmailsDataGrid({
  filter = false,
  email,
  projectID,
  autoHeight,
  value,
  hideHeader,
  hideFooter,
}: {
  filter?: boolean;
  email?: string;
  projectID?: number;
  autoHeight?: boolean;
  value?: Email[];
  hideHeader?: boolean;
  hideFooter?: boolean;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filters = searchParams.get("filters");
  const limit = searchParams.get("limit");
  const offset = searchParams.get("offset");

  const emailFilter =
    email &&
    JSON.stringify([{ name: "sentToEmail", comparison: "eq", value: email }]);

  const _filters = email ? emailFilter : filters;

  const params = {
    filters: _filters,
    offset: offset !== null ? offset : "0",
    limit: limit !== null ? limit : `${EMAILS_PER_PAGE}`,
  };

  const { emails, isLoading: l1 } = useEmails(params, {
    skip: projectID !== undefined || value,
  });

  const rows = value ?? emails;
  const dispatch = useDispatch();
  const pathnames = location.pathname.split("/").filter((x) => x);
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));
  const isEmailsPage = pathnames.indexOf("emails") >= 0;

  const columns = [
    {
      field: "id",
      headerName: "ID",
      type: "number",
      width: 75,
      renderCell: (p) => (
        <Id startDecorator={<i className="fa-solid fa-envelope"></i>}>
          {p.row.id}
        </Id>
      ),
    },
    {
      field: "recipients",
      headerName: "To",
      type: "string",
      width: 250,
      valueGetter: (p) => p.value[0]?.emailAddress,
      renderCell: (p) => {
        return <Typography level="body2">{p.value}</Typography>;
      },
    },
    {
      field: "subject",
      headerName: "Subject",
      type: "string",
      flex: 1,
    },
    {
      field: "sentFromEmail",
      headerName: "Sender",
      type: "string",
      width: 200,
    },
    {
      field: "createdAt",
      headerName: "Date",
      width: 175,
      type: "date",
      valueFormatter: (p) => {
        return moment(p.value).fromNow();
      },
      renderCell: (p) => {
        return (
          <Tooltip title={moment(p.value).format("lll")}>
            <Box sx={{ lineHeight: 1.2, fontSize: 12 }}>
              {moment(p.value).format("lll")}
              <br />
              <span style={{ fontSize: 11 }}>{moment(p.value).fromNow()}</span>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "sent",
      headerName: "Status",
      type: "boolean",
      renderCell: (p) => {
        const sent = p.value;
        return (
          <Tooltip
            title={
              !sent
                ? `${p.row.failCount} attempt${p.row.failCount > 1 ? "s" : ""}`
                : "Email Successfully Sent!"
            }
          >
            <Badge badgeContent={p.row.failCount} color="danger" size="sm">
              <Chip
                size="sm"
                color={sent ? "success" : "warning"}
                variant="soft"
                startDecorator={
                  sent ? (
                    <i className="fa-solid fa-check"></i>
                  ) : (
                    <i className="fa-solid fa-spinner fa-spin-pulse"></i>
                  )
                }
              >
                {sent ? "Sent" : "Pending"}
              </Chip>
            </Badge>
          </Tooltip>
        );
      },
    },
  ];

  const rowsLoading = l1;

  let toolbar = QuickSearchToolbar;
  if (!filter && !email && !projectID) toolbar = CustomToolbar;

  console.log(rows);

  return (
    <DataGridPro
      hideFooter={hideFooter}
      autoHeight={autoHeight}
      loading={rowsLoading}
      density="compact"
      onRowClick={(p) => {
        if (isEmailsPage) {
          navigate(`/emails/${p.row.id}${location.search ?? ""}`);
        }
        // dispatch(setSelectedEmailID(p.row.id));
      }}
      rows={rows ?? []}
      columns={columns}
      disableSelectionOnClick
      components={{
        Toolbar: !hideHeader ? toolbar : undefined,
        Footer: !hideFooter ? CustomFooter : undefined,
      }}
      columnVisibilityModel={{
        id: !sm,
        sentFromEmail: !sm,
      }}
      componentsProps={{
        footer: { isFetching: rowsLoading, count: emails.length },
      }}
      initialState={{
        sorting: {
          sortModel: [
            {
              field: "id",
              sort: "desc",
            },
          ],
        },
      }}
    />
  );
}

function CustomToolbar() {
  return (
    <Hidden smDown>
      <Box
        className="header"
        sx={{
          display: "flex",
          p: 1,
          alignItems: "start",
          gap: 1,
          position: "relative",
          justifyContent: "space-between",
        }}
      >
        <EmailsSearch />
      </Box>
    </Hidden>
  );
}

function CustomFooter(props: { isFetching: boolean; count: number }) {
  const { isFetching, count } = props;
  return (
    <NerdPaginationFooter
      count={count}
      loading={isFetching}
      defaultItemsPerPage={EMAILS_PER_PAGE}
    />
  );
}

function QuickSearchToolbar() {
  return (
    <Box
      className="header"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        p: 1,
        alignItems: "center",
      }}
    >
      <GridToolbarQuickFilter
        size="small"
        fullWidth
        sx={{ flexGrow: 1 }}
        variant="outlined"
      />
    </Box>
  );
}
